import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Save, Loader2, Clock, Server, Cookie, RefreshCw, Shield, Archive, Database, Zap, Globe, Info,
} from "lucide-react";
import { api } from "../lib/api";
import ProvidersPanel from "../components/ProvidersPanel";

// ---------- تب ۱: استعلام اطلاعات هویتی و سیستم ----------
const IDENTITY_GROUPS = [
  {
    title: "رفتار کش و استعلام آنلاین دوره‌ای", icon: Clock,
    fields: [
      { key: "cache_response_delay_ms", label: "تأخیر پاسخ از کش (میلی‌ثانیه)", type: "number", hint: "۰ = بدون تأخیر" },
      { key: "online_periodic_every", label: "استعلام آنلاین از هر N فراخوانی", type: "number", hint: "۰ = غیرفعال" },
    ],
  },
  {
    title: "وب‌سرویس عدل‌ایران", icon: Server,
    fields: [
      { key: "adliran_upstream_url", label: "URL وب‌سرویس", type: "text" },
      { key: "adliran_host", label: "Host هدر", type: "text" },
      { key: "adliran_origin", label: "Origin", type: "text" },
      { key: "adliran_referer", label: "Referer", type: "text" },
      { key: "adliran_user_agent", label: "User-Agent", type: "text" },
    ],
  },
  {
    title: "کوکی و بودجه فراخوانی", icon: Cookie,
    fields: [
      { key: "cookie_ttl_seconds", label: "TTL کوکی (ثانیه)", type: "number" },
      { key: "cookie_max_wait_ms", label: "حداکثر انتظار برای کوکی (ms)", type: "number" },
      { key: "cookie_poll_interval_ms", label: "بازه پول کوکی (ms)", type: "number" },
      { key: "max_attempts", label: "تعداد تلاش‌های مجاز", type: "number" },
      { key: "provider_budget_ms", label: "بودجه کل ارائه‌دهنده (ms)", type: "number" },
      { key: "per_attempt_ms", label: "بودجه هر تلاش (ms)", type: "number" },
    ],
  },
  {
    title: "رفرش خودکار کوکی‌ها", icon: RefreshCw,
    fields: [
      { key: "auto_refresh_enabled", label: "رفرش خودکار فعال باشد", type: "bool" },
      { key: "auto_refresh_interval_seconds", label: "بازه رفرش خودکار (ثانیه)", type: "number" },
      { key: "cookie_refresh_threshold_seconds", label: "آستانه رفرش قبل از انقضا (ثانیه)", type: "number" },
    ],
  },
  {
    title: "احراز و نگه‌داری لاگ", icon: Shield,
    fields: [
      { key: "jwt_expires_hours", label: "مدت اعتبار توکن ادمین (ساعت)", type: "number" },
      { key: "log_retention_default", label: "تعداد رکوردهای نگه‌داری لاگ", type: "number" },
    ],
  },
  {
    title: "بکاپ و ارسال به بله", icon: Archive,
    fields: [
      { key: "backup_enabled", label: "بکاپ خودکار فعال باشد", type: "bool" },
      { key: "backup_interval_hours", label: "بازه بکاپ خودکار (ساعت)", type: "number" },
      { key: "backup_chunk_size", label: "اندازه هر قسمت (تعداد رکورد در هر فایل)", type: "number", hint: "پیشنهاد ۱۰۰٬۰۰۰ تا ۵۰۰٬۰۰۰ — بستگی به قدرت سرور دارد" },
      { key: "backup_use_final", label: "استفاده از FINAL هنگام بکاپ (کند اما دقیق)", type: "bool" },
      { key: "bale_bot_token", label: "توکن ربات بله", type: "text", secret: true },
      { key: "bale_chat_id", label: "شناسه عددی کاربر مقصد در بله", type: "text" },
    ],
  },
];

// ---------- تب ۲: زنجیره‌ی شاهکار ----------
const SHAHKAR_GROUPS = [
  {
    title: "زنجیره‌ی پروایدرها", icon: Zap,
    fields: [
      { key: "shahkar_total_timeout_ms", label: "مهلت کل انتظار برای پاسخ شاهکار (ms)", type: "number",
        hint: "بعد از این مدت، زنجیره متوقف می‌شود و پاسخ خطا برمی‌گردد" },
      { key: "shahkar_max_failures", label: "قطع خودکار پروایدر بعد از N خطای پیاپی", type: "number",
        hint: "۰ = هرگز قطع نشود" },
    ],
  },
];

// ---------- تب ۳: ثنا ----------
const SANA_GROUPS = [
  {
    title: "استعلام عضویت در ثنا", icon: Globe,
    fields: [
      { key: "sana_membership_enabled", label: "اندپوینت عضویت در ثنا فعال باشد", type: "bool" },
    ],
  },
];

const TABS = [
  { key: "identity", label: "هویت و سیستم", icon: Database, groups: IDENTITY_GROUPS },
  { key: "shahkar", label: "شاهکار", icon: Zap, groups: SHAHKAR_GROUPS, custom: true },
  { key: "sana", label: "ثنا", icon: Globe, groups: SANA_GROUPS },
];

const ALL_FIELDS = [...IDENTITY_GROUPS, ...SHAHKAR_GROUPS, ...SANA_GROUPS].flatMap((g) => g.fields);

// اگر یک بخش خطا بدهد، فقط همان بخش پیام خطا نشان می‌دهد و کل صفحه سفید نمی‌شود
class SectionBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          این بخش خطا داد: {String(this.state.error?.message || this.state.error)}. صفحه را رفرش کنید و اگر تکرار شد، این پیام را گزارش دهید.
        </div>
      );
    }
    return this.props.children;
  }
}

export default function Settings() {
  const [tab, setTab] = useState("identity");
  const [values, setValues] = useState({});
  const [saved, setSaved] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings")
      .then(({ data }) => {
        const s = data.settings || {};
        setValues(s);
        setSaved(s);
      })
      .catch(() => toast.error("خطا در دریافت تنظیمات"))
      .finally(() => setLoading(false));
  }, []);

  const dirty = useMemo(
    () => ALL_FIELDS.filter((f) => String(values[f.key] ?? "") !== String(saved[f.key] ?? "")),
    [values, saved]
  );

  const setVal = (key, v) => setValues((s) => ({ ...s, [key]: v }));

  const save = async () => {
    if (dirty.length === 0) return toast.info("تغییری وجود ندارد");
    const payload = Object.fromEntries(ALL_FIELDS.map((f) => [f.key, values[f.key] ?? ""]));
    setSaving(true);
    try {
      await api.put("/admin/settings", { settings: payload });
      setSaved(values);
      toast.success(`${dirty.length} تنظیم ذخیره شد`);
    } catch {
      toast.error("خطا در ذخیره تنظیمات");
    }
    setSaving(false);
  };

  const current = TABS.find((t) => t.key === tab);
  const dirtyInTab = dirty.filter((f) => current.groups.some((g) => g.fields.includes(f))).length;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-6 max-w-5xl">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">تنظیمات</h1>
        <p className="text-sm text-slate-500 mt-1">هر تب تنظیمات مربوط به خودش را دارد. ذخیره‌ی هر تب همه‌ی تغییرات را یکجا ثبت می‌کند.</p>
      </div>

      <div className="flex gap-2 p-1 bg-slate-100 rounded-2xl w-fit flex-wrap" role="tablist">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          const count = dirty.filter((f) => t.groups.some((g) => g.fields.includes(f))).length;
          return (
            <button key={t.key} role="tab" aria-selected={active} data-testid={`settings-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2.5 rounded-xl text-sm flex items-center gap-2 transition ${
                active ? "bg-white shadow-sm text-slate-900 font-semibold" : "text-slate-500 hover:text-slate-800"}`}>
              <Icon size={16} /> {t.label}
              {count > 0 && <span className="text-[11px] bg-amber-100 text-amber-800 rounded-full px-1.5">{count}</span>}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="p-10 text-center"><Loader2 className="animate-spin inline" /></div>
      ) : (
        <div className="space-y-6">
          {current.groups.map((g) => {
            const Icon = g.icon;
            return (
              <section key={g.title} className="bg-white border border-slate-200/80 rounded-2xl shadow-sm p-6 space-y-5">
                <header className="flex items-center gap-3 pb-3 border-b border-slate-100">
                  <div className="w-9 h-9 rounded-lg bg-slate-50 border border-slate-200 grid place-items-center text-slate-600">
                    <Icon size={16} />
                  </div>
                  <h2 className="font-semibold text-slate-800">{g.title}</h2>
                </header>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                  {g.fields.map((f) => (
                    <Field key={f.key} f={f} value={values[f.key] ?? ""} onChange={(v) => setVal(f.key, v)} />
                  ))}
                </div>
              </section>
            );
          })}

          {current.key === "shahkar" && (
            <section className="bg-white border border-slate-200/80 rounded-2xl shadow-sm p-6">
              <SectionBoundary>
                <ProvidersPanel />
              </SectionBoundary>
            </section>
          )}

          {current.key === "sana" && (
            <div className="flex gap-3 items-start rounded-2xl border border-sky-200 bg-sky-50/60 p-4 text-sm text-sky-900">
              <Info size={18} className="mt-0.5 shrink-0" />
              <div>
                کوکی‌های ثنا برای هر اکانت از صفحه‌ی <b>اکانت‌ها</b> ساخته و تازه می‌شوند.
              </div>
            </div>
          )}
        </div>
      )}

      <div className="sticky bottom-0 bg-white/90 backdrop-blur border-t border-slate-200 py-3 flex items-center justify-between gap-3">
        <span className="text-xs text-slate-500">
          {dirty.length > 0 ? `${dirty.length} تغییر ذخیره‌نشده (${dirtyInTab} در این تب)` : "همه‌ی تغییرات ذخیره شده‌اند"}
        </span>
        <button data-testid="settings-save-button" onClick={save} disabled={saving || dirty.length === 0}
          className="px-6 py-2.5 rounded-xl bg-teal-600 hover:bg-teal-700 text-white flex items-center gap-2 shadow-sm disabled:opacity-50">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          ذخیره تنظیمات
        </button>
      </div>
    </motion.div>
  );
}

function Field({ f, value, onChange }) {
  if (f.type === "bool") {
    const isTrue = String(value).toLowerCase() === "true";
    return (
      <div>
        <label className="text-sm font-medium text-slate-700 mb-1.5 block">{f.label}</label>
        <button type="button" data-testid={`setting-${f.key}`}
          onClick={() => onChange(isTrue ? "false" : "true")}
          className={`px-4 py-2 rounded-xl text-sm font-semibold border transition-all ${
            isTrue ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
          {isTrue ? "فعال" : "غیرفعال"}
        </button>
        {f.hint && <p className="text-xs text-slate-400 mt-1.5">{f.hint}</p>}
      </div>
    );
  }
  return (
    <div>
      <label className="text-sm font-medium text-slate-700 mb-1.5 block">{f.label}</label>
      <input data-testid={`setting-${f.key}`} type={f.secret ? "password" : f.type} value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-xl border border-slate-300 focus:border-teal-500 outline-none text-sm" />
      {f.hint && <p className="text-xs text-slate-400 mt-1">{f.hint}</p>}
    </div>
  );
}
